import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FormGroup, Input } from "reactstrap";
import Field from "../../../components/Field";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Switch from "../../../components/Inputs/Switch";
import DateInput from "../../../components/Inputs/DateInput";
import CustomTimeInput from "../../../components/Inputs/CustomTimeInput";
import Button from "../../../components/Button";
import PaidHours from "./PaidHours";
import StatisticBox from "./StatisticBox";
import { renderSettingsResourceLabel } from "../../../utils/helpers/settings";
import { generateErrorsConfigForForm } from "../../../utils/helpers/errors";
import { firstToUpper } from "../../../utils/helpers/string";
import { useUser } from "../../../utils/hooks/user";
import { combineDateAndTime } from "../../../utils/helpers/date";
import { fitsBalance, fitsMaxTaken, getHoursTaken, getMaxRequestedHours, getMinBalance, isInProbation, isMultipleOfMinRequested } from "../../../utils/helpers/timeOffRules";
import "./_style.scss";

// probation on the day the absence starts (today when no date is picked yet), read as the
// backend does: value >= days since the hire (or seniority) date, no date = not in probation
export const isInProhibition = (plan, user, date) => {
	const prohibition = plan?.plan?.prohibition;
	if (!prohibition) {
		return { inProhibition: false, allowRequests: true };
	}
	const day = date ? moment(date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
	return {
		inProhibition: isInProbation(plan.plan, user, day),
		allowRequests: prohibition.allowRequests,
	};
}

const getSettingsPlanSearchFilters = (search, startDate, endDate) => {
	let filters = {};
	if(!search) {
		filters = {
			fields: ["id", "code", "description"],
			filterType: "or",
			withoutLookups: "true",
		};
	}
	
	filters = {
		"filter[code][method]": "contains",
		"filter[code][value]": search,
		"filter[description][method]": "contains",
		"filter[description][value]": search,
		fields: ["id", "code", "description"],
		filterType: "or",
		withoutLookups: "true",
	}
	
	if(startDate && endDate) {
		filters.startDate = moment(startDate).format("YYYY-MM-DD");
		filters.endDate = moment(endDate).format("YYYY-MM-DD");
	}
	
	return filters;
};


const AddAbsenceForm = ({ userId, onFinish, onClose, values, error, loading, mode }) => {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const [formData, setFormData] = useState();

	const user = useUser();
	const manager = useMemo(() => {
		let u = user;
		if (mode === "view") {
			u = values.user;
		}
		return u?.supervisors?.find((supervisor) => supervisor.primary);
	}, [user, mode, values.user]);

	const today = useMemo(() => {
		return moment().format();
	}, []);

	const onAllDayChange = useCallback(() => {
		form.setFieldsValue({
			date: undefined,
			startDate: undefined,
			endDate: undefined,
		});
	}, [form]);

	const getEndDate = useCallback(({ endDate, startDate, plan }) => {
		const minRequestedHours = Number(plan?.plan?.minRequestedHours || 0) * 3600;

		const diff = moment(endDate).diff(moment(startDate), "seconds");
		if (diff % minRequestedHours !== 0) {
			return moment(endDate).add(Number(minRequestedHours - (diff % minRequestedHours)), "seconds");
		}
		return endDate;
	}, []);

	const submit = useCallback((values) => {
		setFormData(values);
		onFinish(values);
	}, [onFinish, setFormData])

	useEffect(() => {
		if (!values || formData) {
			return;
		}

		const data = values["data"];
		form.setFieldsValue({
			plan: data.plan,
			type: data.type,
			allDay: data.allDay,
			startDate:
				data.startDate &&
				(data.allDay
					? moment.parseZone(data?.startDate).local(true).toDate()
					: moment.parseZone(data?.startDate)),
			endDate:
				data.endDate &&
				(data.allDay
					? moment.parseZone(data?.endDate).local(true).toDate()
					: moment.parseZone(data?.endDate)),
			date: !data.allDay && moment.parseZone(data?.startDate).local(true).toDate(),
			note: data.note,
		});
	}, [form, values, formData]);

	useEffect(() => {
		if (formData) {
			form.setFieldsValue(formData);
		}
	}, [form, formData]);

	useEffect(() => {
		let _error;
		if (typeof error === "object" && error !== null) {
			_error = {
				...error,
				startDate: error.start,
				endDate: error.end,
			};
		} else {
			_error = error;
		}
		const fieldErrors = generateErrorsConfigForForm(["date", "startDate", "endDate"], _error);
		form.setFields(fieldErrors);
	}, [form, error]);

	return (
		<Form form={form} onFinish={submit} className="d-flex flex-column h-100">
			<div className="p-4 content flex-grow-1 overflow-auto">
				<div className="mt-2">
					<BaseField shouldUpdate>
						{({ }, { }, { getFieldValue }) => {
							const allDay = getFieldValue("allDay");
							const date = getFieldValue("date");
							const startDate = getFieldValue("startDate");
							const endDate = getFieldValue("endDate");
							const plan = getFieldValue("plan");
							const currentPending = values?.id && mode !== "view" ? plan?.pendingItems?.find(request => request.id === values?.id) : undefined;
							const balance = Number((plan?.bucket?.available || 0) + (currentPending?.total || 0)).toFixed(2);
							const allDayDiff = (allDay && startDate && endDate) && (moment(endDate, "YYYY-MM-DD").diff(moment(startDate, "YYYY-MM-DD"), "day") + 1) * 8;
							const startTime = (startDate && date) && combineDateAndTime(moment(date), moment(startDate));
							const endTime = (endDate && date) && combineDateAndTime(moment(date), moment(endDate));
							const diff = (!allDay && startTime && endTime) && moment(endTime).diff(moment(startTime), "hours", true);
							// a negative minimum balance lets the balance go below 0, a positive one has to stay
							const minBalance = getMinBalance(plan?.plan);
							const requested = allDay ? allDayDiff : diff;
							const hasError = !!requested && !fitsBalance(plan?.plan, balance, requested);
							// "Maximum hours allowed to be taken" is a plan year limit: what the plan already took,
							// without this request's own pending hours when it is edited, plus this request
							const maxRequestedHours = plan ? getMaxRequestedHours(plan.plan, user, allDay ? startDate : date) : undefined;
							const taken = plan ? Number((getHoursTaken(plan, [plan]) - (currentPending?.total || 0)).toFixed(2)) : 0;
							const hasMaxTakenError = !!requested && !fitsMaxTaken(maxRequestedHours, taken, requested);
							const { inProhibition, allowRequests } = isInProhibition(plan, user, allDay ? startDate : date);
							const minRequestedHours = Number(plan?.plan?.minRequestedHours || 0);
							// an all day absence books 8 hours a day: each day must be a multiple of the minimum
							const hasAllDayMinError = allDay && !!plan && !isMultipleOfMinRequested(plan?.plan, 8);

							return (
								<>
									<Field
										name="plan"
										label={t("plan")}
										rules={[
											{
												required: true,
												message: t("required-plan"),
											},
										]}
									>
										<ResourceSelect
											renderLabel={({ plan }) => renderSettingsResourceLabel(plan)}
											valuePropName="id"
											resourcePath={`/profile/time-off/plan?status=active`}
											placeholder={t("plan")}
											hasSearch
											getSearchFilters={(value) => getSettingsPlanSearchFilters(value, startDate, endDate)}
											disabled={!!mode}
											onChange={(value) => {
												if (!allDay) {
													form.setFieldValue(
														"endDate", 
														getEndDate({ plan: value, endDate, startDate })
													);
												}
											}}
										/>
									</Field>

									{(mode !== "view" && inProhibition) && (
										<div
											className={`rounded px-3 py-2 mb-3 ${allowRequests ? "text-orange" : "text-danger"}`}
											style={{ backgroundColor: allowRequests ? "#ffa50021" : "#ff00000a" }}
										>
											{allowRequests
												? t("you-are-on-probation-period")
												: t("not-allowed-to-make-request")}
										</div>
									)}

									<Field name="allDay" valuePropName="checked">
										<Switch onChange={onAllDayChange} disabled={!!mode}>{t("all-day")}</Switch>
									</Field>

									{allDay ? (
										<div className="form-row">
											<div className="form-group col-md-6">
												<label htmlFor="startDate" className="form-control-label">
													{t("from")}
												</label>
												<Field
													className="mb-0"
													name="startDate"
													rules={[
														{
															required: true,
															message: t("required-from"),
														},
														{
															validator(_, value) {
																if (
																	moment(value).isBefore(moment(), "date")
																) {
																	return Promise.reject(
																		new Error(
																			t("from-before-current-date")
																		)
																	);
																}
																// if (moment(value).year() > moment().year()) {
																// 	return Promise.reject(
																// 		new Error(
																// 			t("from-after-current-year")
																// 		)
																// 	);
																// }
																return Promise.resolve();
															},
														},
													]}
												>
													{({ value, onChange }) => (
														<DateInput
															placeholderText={t("from")}
															disabled={!!mode}
															wrapperClassName="w-100"
															value={value}
															filterDate={(date) => {
																return moment(date).isAfter(moment(today), "day");
															}}
															onChange={(date) => {
																onChange(date);
																const endDate = getFieldValue("endDate");
																if (endDate && moment(endDate).isBefore(moment(date), "date")) {
																	form.setFieldValue("endDate", date);
																}
															}}
														/>
													)}
												</Field>
											</div>

											<div className="form-group col-md-6">
												<label htmlFor="endDate" className="form-control-label">
													{firstToUpper(t("to"))}
												</label>
												<Field
													className="mb-0"
													name="endDate"
													dependencies={["startDate"]}
													rules={[
														{
															required: true,
															message: t("required-to"),
														},
														{
															validator(_, value) {
																const from = getFieldValue("startDate");
																if (
																	moment(value).isBefore(
																		moment(from),
																		"date"
																	)
																) {
																	return Promise.reject(
																		new Error(t("to-bigger-than-from"))
																	);
																}
																return Promise.resolve();
															},
														},
													]}
												>
													<DateInput
														placeholderText={firstToUpper(t("to"))}
														disabled={!!mode || !startDate}
														wrapperClassName="w-100"
														{...(startDate && {
															minDate: moment(startDate).startOf("day").toDate(),
														})}
														filterDate={(date) => {
															return moment(date).isAfter(moment(today), "day")
																&& (!startDate || !moment(date).isBefore(moment(startDate), "date"));
														}}
													/>
												</Field>
											</div>
										</div>
									) : (
										<div className="d-flex flex-column justify-content-between">
											<Field
												label={t("date")}
												name="date"
												rules={[
													{
														required: true,
														message: t("required-date"),
													},
												]}
												className="w-100 d-flex flex-column"
											>
												<DateInput
													className="w-100"
													disabled={!!mode}
													filterDate={(date) => {
														return moment(date).isAfter(moment(today), "day");
													}}
													placeholderText={t("date")}
												/>
											</Field>

											<Field
												label={t("from")}
												name="startDate"
												rules={[
													{
														required: true,
														message: t("required-from"),
													},
												]}
											>
												<CustomTimeInput
													placeholder={t("from")}
													disabled={!!mode}
												/>
											</Field>

											<Field
												label={firstToUpper(t("to"))}
												name="endDate"
												dependencies={["startDate"]}
												rules={[
													{
														required: true,
														message: t("required-to"),
													},
													{
														validator(_, value) {
															const from = getFieldValue("startDate");
															if (value) {
																const diff = moment(value).diff(moment(from), "seconds");
																if (
																	moment(value).isSameOrBefore(
																		moment(from),
																		"seconds"
																	)
																) {
																	return Promise.reject(
																		new Error(t("to-bigger-than-from"))
																	);
																} else if (diff < Number(minRequestedHours * 3600)) {
																	return Promise.reject(
																		new Error(t("request-min-duration", { hours: minRequestedHours }))
																	);
																} else if (!isMultipleOfMinRequested(plan?.plan, diff / 3600)) {
																	return Promise.reject(
																		new Error(t("duration-not-multiple-of-min-requested-hours"))
																	);
																} else {
																	return Promise.resolve();
																}
															}
															return Promise.resolve();
														},
													},
												]}
											>
												{({ value, onChange }) => {
													return (
														<CustomTimeInput
															placeholder={firstToUpper(t("to"))}
															disabled={!!mode}
															value={value}
															onChange={(value) => onChange(getEndDate({ endDate: value, plan, startDate }))}
														/>
													);
												}}
											</Field>
										</div>
									)}

									{hasError && mode !== "view" && (
										<div className="invalid-feedback d-block mb-1">
											{t("duration-bigger-than-balance", { balance: minBalance ? `${balance} (min ${minBalance})` : balance })}
										</div>
									)}

									{hasMaxTakenError && mode !== "view" && (
										<div className="invalid-feedback d-block mb-1">
											{t("max-hours-taken-exceeded", { taken, max: maxRequestedHours })}
										</div>
									)}

									{hasAllDayMinError && mode !== "view" && (
										<div className="invalid-feedback d-block mb-1">
											{t("duration-not-multiple-of-min-requested-hours")}
										</div>
									)}
								</>
							);
						}}
					</BaseField>

					<PaidHours />
				</div>

				<FormGroup className="mb-0">
					<label className="form-control-label" htmlFor="exampleFormControlTextarea3">
						{t("attach-request-note")}
					</label>

					<Field name="note">
						<Input
							id="exampleFormControlTextarea3"
							resize="none"
							rows="7"
							type="textarea"
							className="h-100"
							disabled={!!mode}
						/>
					</Field>

					<footer className="note-border p-2 border-top-0 text-sm mt-2">
						*{t("requests-send-for-manager-approval")}
					</footer>
				</FormGroup>

				<div className="mb-3">
					<h5 className="mt-3">{t("manager-receiving-request")}</h5>
					{manager ? (
						<p>
							{manager.firstName} {manager.lastName}
						</p>
					) : (
						<p>{t("no-managers-available")}</p>
					)}
				</div>

				<BaseField shouldUpdate>
					{({ }, { }, { getFieldValue }) => {
						const allDay = getFieldValue("allDay");
						const date = getFieldValue("date");
						const startDate = getFieldValue("startDate");
						const endDate = getFieldValue("endDate");
						const plan = getFieldValue("plan");

						const allDayDiff = (allDay && startDate && endDate) && (moment(endDate, "YYYY-MM-DD").diff(moment(startDate, "YYYY-MM-DD"), "day") + 1) * 8;
						const startTime = (startDate && date) && combineDateAndTime(moment(date), moment(startDate));
						const endTime = (endDate && date) && combineDateAndTime(moment(date), moment(endDate));
						const diff = (!allDay && startTime && endTime)
							&& Number(moment(endTime).diff(moment(startTime), "hours", true).toFixed(1));
						const value = allDay ? allDayDiff : diff;
						// const startD = values?.data?.startDate
						// 	? (!values?.data?.allDay && values?.data?.date)
						// 		? combineDateAndTime(moment(values.data.date), moment(values?.data?.startDate))
						// 		: moment.parseZone(values.data.startDate).format("YYYY-MM-DD")
						// 	: undefined;

						// const endD = values?.data?.endDate
						// 	? (!values?.data?.allDay && values?.data?.date)
						// 		? combineDateAndTime(moment(values.data.date), moment(values?.data?.endDate))
						// 		: moment.parseZone(values.data.endDate).format("YYYY-MM-DD")
						// 	: undefined;

						// const allDayChange = (startD && endD && startDate && endDate && allDay)
						// 	? !moment(startD).isSame(moment(startDate), "date") || !moment(endD).isSame(moment(endDate), "date")
						// 	: false;
						// const change = (startD && endD && startTime && endTime && !allDay)
						// 	? !moment(startD).isSame(moment(startTime), "seconds") || !moment(endD).isSame(moment(endTime), "seconds")
						// 	: false;

						// const hasChanges = values?.data?.startDate ? (allDayChange || change) : true;

						if (!plan) {
							return;
						}
						const currentPending = values?.id && mode !== "view" ? plan?.pendingItems?.find(request => request.id === values?.id) : undefined;

						const availableHours = Number((plan?.bucket?.available || 0) + (mode !== "view" ? (currentPending?.total || 0) : 0) - (mode !== "view" ? (value || 0) : 0));
						return (
							<div className="mb-5">
								<div className="row">
									<div className="col-md-6">
										<StatisticBox label={t("total-hours")} value={Number(plan?.bucket?.total || 0).toFixed(2)} />
									</div>
									<div className="col-md-6">
										<StatisticBox
											label={t("available-hours")}
											value={Number(availableHours).toFixed(2)}
										/>
									</div>
								</div>
								<div className="row">
									<div className="col-md-6">
										<StatisticBox label={t("taken-hours")} value={Number(plan?.bucket?.consumed || 0).toFixed(2)} />
									</div>
									<div className="col-md-6">
										<StatisticBox
											label={t("planned-hours")}
											value={(Number((plan?.bucket?.pending || 0) - (currentPending?.total || 0)) + (mode !== "view" ? (value || 0) : 0)).toFixed(2)}
										/>
									</div>
								</div>
							</div>
						);
					}}
				</BaseField>
			</div>

			{!mode && (
				<div className="border-top py-3 text-center align-items-center justify-content-center">
					<Button
						className="btn-icon text-muted"
						type="button"
						color="white"
						onClick={onClose}
						loading={loading}
					>
						<span className="btn-inner--icon">
							<FontAwesomeIcon icon={faCircleXmark} />
						</span>
						<span className="btn-inner--text">{t("cancel")}</span>
					</Button>

					<Button
						className="btn-icon text-info"
						type="submit"
						color="white"
						disabled={loading}
					>
						<span className="btn-inner--icon">
							<FontAwesomeIcon icon={faCircleCheck} />
						</span>

						<span className="btn-inner--text">
							{t("send-for-approval")}
						</span>
					</Button>
				</div>
			)}
		</Form>
	);
};

export default AddAbsenceForm;
